import React from 'react';
import { Info, Activity, Heart, Zap, Clock, Shield, Smartphone } from 'lucide-react';

/**
 * Компонент с информацией о целевом пульсе и тренировках
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-red-500" />
        О целевом пульсе и кардиотренировках
      </h2>
      
      <div className="prose prose-red dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Что такое целевой пульс (THR)?
        </h3>
        <p>
          <strong>Целевой пульс</strong> (Target Heart Rate, THR) — это диапазон частоты сердечных сокращений, при котором тренировка наиболее эффективна для достижения ваших целей. Тренировка в определенной пульсовой зоне обеспечивает оптимальную нагрузку на сердечно-сосудистую систему и позволяет достичь желаемых результатов — будь то похудение, развитие выносливости или восстановление.
        </p>
        <p>
          В России широко используется система расчета целевых зон пульса как процент от максимального пульса. При этом для более точных расчетов применяется методика Карвонена, которая учитывает не только максимальный, но и пульс в состоянии покоя.
        </p>

        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-red-500" />
          Пульсовые зоны и их назначение
        </h3>
        <div className="overflow-x-auto my-4">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-red-50 dark:bg-red-900/20">
                <th className="px-4 py-2 text-left text-sm font-semibold text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">Зона</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">% от максимального пульса</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">Назначение</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">Источник энергии</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Зона восстановления</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">50-60%</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Разминка, заминка, восстановление</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Преимущественно жиры</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-700/30">
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Зона жиросжигания</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">60-70%</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Снижение веса, улучшение базовой выносливости</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">60-80% жиры, 20-40% углеводы</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Аэробная зона</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">70-80%</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Кардиотренировка, повышение выносливости</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">50% жиры, 50% углеводы</td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-700/30">
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Анаэробная зона</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">80-90%</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Повышение скорости, анаэробный порог</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">20% жиры, 80% углеводы</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Максимальная зона</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">90-100%</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Максимальная производительность, интервалы высокой интенсивности</td>
                <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">Преимущественно углеводы</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Исследования российских специалистов
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            По данным НМИЦ ТПМ Минздрава России, тренировки с контролем пульса значительно улучшают работу сердечно-сосудистой системы. Для большинства взрослых россиян рекомендуется 150 минут умеренной аэробной активности в неделю (60-70% от максимального пульса) или 75 минут высокоинтенсивной (70-85%).
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-red-500" />
          Расчет максимального пульса
        </h3>
        <p>
          Существует несколько формул для расчета максимального пульса:
        </p>
        <ul>
          <li>
            <strong>Формула Танака (2001)</strong>: 208 - 0.7 × возраст. Рекомендована Российским кардиологическим обществом как наиболее точная и современная.
          </li>
          <li>
            <strong>Классическая формула (220 - возраст)</strong>: Проста в использовании, но менее точна, особенно для пожилых людей и тренированных спортсменов.
          </li>
          <li>
            <strong>Формула Геллиша</strong>: 207 - 0.7 × возраст. Часто применяется в российских спортивных учреждениях для профессиональных спортсменов.
          </li>
        </ul>
        <p>
          Важно помнить, что максимальный пульс зависит от многих факторов, включая генетику, уровень физической подготовки и состояние здоровья, поэтому расчетные значения могут отличаться от фактических.
        </p>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-red-500" />
          Как долго тренироваться в целевой зоне?
        </h3>
        <p>
          Согласно рекомендациям Министерства здравоохранения РФ и Российского общества кардиологов:
        </p>
        <ul>
          <li>Для снижения веса эффективны тренировки в зоне жиросжигания (60-70%) продолжительностью от 40 до 60 минут</li>
          <li>Для улучшения работы сердечно-сосудистой системы рекомендуются тренировки в аэробной зоне (70-80%) по 20-40 минут</li>
          <li>Интервальные тренировки с чередованием аэробной и анаэробной зон (например, 2 минуты в зоне 80-90% и 3 минуты в зоне 60-70%) особенно эффективны для опытных спортсменов</li>
          <li>Для начинающих рекомендуется постепенное увеличение времени тренировки, начиная с 10-15 минут в зоне восстановления</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Внимание: состояние здоровья и пульс
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>При гипертонии рекомендуется снижение верхней границы целевого пульса на 10-15 уд/мин</li>
            <li>При сердечно-сосудистых заболеваниях тренировки должны проводиться только под наблюдением врача</li>
            <li>Принимаемые лекарства (особенно бета-блокаторы) могут влиять на частоту пульса</li>
            <li>Людям старше 65 лет перед началом тренировок рекомендуется пройти нагрузочный тест</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-red-500" />
          Измерение пульса: методы и устройства
        </h3>
        <p>
          Существует несколько способов измерения пульса во время тренировок:
        </p>
        <ul>
          <li>
            <strong>Ручное измерение</strong>: найдите пульс на запястье или шее и посчитайте удары за 15 секунд, затем умножьте на 4. Метод прост, но требует остановки тренировки.
          </li>
          <li>
            <strong>Нагрудные пульсометры</strong>: надежные и точные устройства, которые крепятся на груди с помощью эластичного ремня. Предпочтительны для интенсивных тренировок.
          </li>
          <li>
            <strong>Фитнес-браслеты и смарт-часы</strong>: удобны в повседневном использовании, но могут быть менее точными при высокоинтенсивных тренировках.
          </li>
          <li>
            <strong>Датчики на кардиотренажерах</strong>: встроенные в тренажеры датчики (обычно на рукоятках) обеспечивают приблизительное измерение.
          </li>
        </ul>
        <p>
          На российском рынке представлены пульсометры различных брендов, включая Polar, Garmin, Sigma и Suunto. При выборе устройства обратите внимание на точность измерений, удобство ношения и дополнительные функции.
        </p>

        <h3>Рекомендации для начинающих</h3>
        <ol>
          <li><strong>Начинайте постепенно</strong> с низкой интенсивности в зоне восстановления (50-60%)</li>
          <li><strong>Определите цель тренировок</strong> (похудение, выносливость, здоровье) и соответствующую целевую зону</li>
          <li><strong>Следите за ощущениями</strong>: при головокружении, одышке или боли снизьте интенсивность</li>
          <li><strong>Постепенно увеличивайте нагрузку</strong>, добавляя по 5 минут каждую неделю</li>
          <li><strong>Соблюдайте регулярность</strong> - 3-5 тренировок в неделю с днями отдыха между ними</li>
          <li><strong>Варьируйте интенсивность</strong>: чередуйте тренировки в разных пульсовых зонах</li>
        </ol>

        <p className="font-semibold mt-6">
          Используйте наш калькулятор целевого пульса для расчета ваших индивидуальных зон и оптимизации тренировок!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;